import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	Switch,
	Button,
	Typography,
	Box,
	Divider,
	Alert,
} from "@mui/material";
import {
	getUserPreferences,
	saveUserPreferences,
	resetUserPreferences,
	setTourEnabled,
	setAutoStartTours,
	setShowWelcome,
	isTourEnabled,
	shouldAutoStartTours,
	shouldShowWelcome,
} from "../../utils/userPreferences";
import {
	resetVisitedPages,
	getVisitedPages,
	PAGE_NAMES,
} from "../../utils/firstTimeVisit";

const TourSettingsDialog = ({ open, onClose }) => {
	const [preferences, setPreferences] = useState({
		tourEnabled: true,
		autoStartTours: false, // Changed to false to match new default
		showWelcome: true,
	});
	const [visitedPages, setVisitedPages] = useState({});
	const [showResetConfirm, setShowResetConfirm] = useState(false);

	useEffect(() => {
		if (open) {
			// Load current preferences
			setPreferences({
				tourEnabled: isTourEnabled(),
				autoStartTours: shouldAutoStartTours(),
				showWelcome: shouldShowWelcome(),
			});

			// Load visited pages info
			setVisitedPages(getVisitedPages());
		}
	}, [open]);

	const handlePreferenceChange = (key, value) => {
		setPreferences((prev) => ({
			...prev,
			[key]: value,
		}));
	};

	const handleSave = () => {
		// Save individual preferences
		setTourEnabled(preferences.tourEnabled);
		setAutoStartTours(preferences.autoStartTours);
		setShowWelcome(preferences.showWelcome);

		onClose();
	};

	const handleResetTours = () => {
		setShowResetConfirm(true);
	};

	const confirmResetTours = () => {
		resetVisitedPages();
		setVisitedPages({});
		setShowResetConfirm(false);

		// Show success message briefly
		setTimeout(() => {
			onClose();
		}, 1000);
	};

	const getPageDisplayName = (pageKey) => {
		const names = {
			[PAGE_NAMES.MAINSCREEN]: "Landing Page",
			[PAGE_NAMES.SUPPLY_TYPE]: "Supply Type Allocation",
			[PAGE_NAMES.VFF]: "Vehicle Tracking",
			[PAGE_NAMES.SERIES]: "Series Tracking",
			[PAGE_NAMES.DASHBOARD]: "Dashboard",
		};
		return names[pageKey] || pageKey;
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 2,
					fontFamily: "MAN EUROPE_Regular",
				},
			}}
		>
			<DialogTitle
				sx={{
					fontFamily: "MAN EUROPE_Bold",
					backgroundColor: "#1e2832",
					color: "white",
					mb: 0,
				}}
			>
				Tour & Help Settings
			</DialogTitle>

			<DialogContent sx={{ mt: 2 }}>
				<Typography
					variant="h6"
					sx={{
						fontFamily: "MAN EUROPE_Bold",
						mb: 2,
						color: "#1e2832",
					}}
				>
					Tour Preferences
				</Typography>

				<Box sx={{ mb: 3 }}>
					<FormControlLabel
						control={
							<Switch
								checked={preferences.tourEnabled}
								onChange={(e) =>
									handlePreferenceChange("tourEnabled", e.target.checked)
								}
								sx={{
									"& .MuiSwitch-switchBase.Mui-checked": {
										color: "#cc0000",
									},
									"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
										backgroundColor: "#cc0000",
									},
								}}
							/>
						}
						label={
							<Box>
								<Typography
									variant="body1"
									sx={{ fontFamily: "MAN EUROPE_Bold" }}
								>
									Enable Tours
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
									}}
								>
									Show interactive tours and help guides
								</Typography>
							</Box>
						}
					/>
				</Box>

				<Box sx={{ mb: 3 }}>
					<FormControlLabel
						control={
							<Switch
								checked={preferences.autoStartTours}
								onChange={(e) =>
									handlePreferenceChange("autoStartTours", e.target.checked)
								}
								disabled={!preferences.tourEnabled}
								sx={{
									"& .MuiSwitch-switchBase.Mui-checked": {
										color: "#cc0000",
									},
									"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
										backgroundColor: "#cc0000",
									},
								}}
							/>
						}
						label={
							<Box>
								<Typography
									variant="body1"
									sx={{ fontFamily: "MAN EUROPE_Bold" }}
								>
									Auto-start Tours
								</Typography>{" "}
								<Typography
									variant="body2"
									sx={{
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
									}}
								>
									Automatically show tours when visiting pages for the first
									time (disabled by default)
								</Typography>
							</Box>
						}
					/>
				</Box>

				<Box sx={{ mb: 3 }}>
					<FormControlLabel
						control={
							<Switch
								checked={preferences.showWelcome}
								onChange={(e) =>
									handlePreferenceChange("showWelcome", e.target.checked)
								}
								disabled={!preferences.tourEnabled}
								sx={{
									"& .MuiSwitch-switchBase.Mui-checked": {
										color: "#cc0000",
									},
									"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
										backgroundColor: "#cc0000",
									},
								}}
							/>
						}
						label={
							<Box>
								<Typography
									variant="body1"
									sx={{ fontFamily: "MAN EUROPE_Bold" }}
								>
									Show Welcome Messages
								</Typography>
								<Typography
									variant="body2"
									sx={{
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
									}}
								>
									Display welcome messages in tours and help content
								</Typography>
							</Box>
						}
					/>
				</Box>

				<Divider sx={{ my: 3 }} />

				<Typography
					variant="h6"
					sx={{
						fontFamily: "MAN EUROPE_Bold",
						mb: 2,
						color: "#1e2832",
					}}
				>
					Tour History
				</Typography>

				<Box sx={{ mb: 2 }}>
					<Typography
						variant="body2"
						sx={{
							color: "#666",
							fontFamily: "MAN EUROPE_Regular",
							mb: 1,
						}}
					>
						Pages you have visited:
					</Typography>

					{Object.keys(visitedPages).length > 0 ? (
						<Box sx={{ ml: 2 }}>
							{Object.entries(visitedPages).map(([pageKey, info]) => (
								<Box key={pageKey} sx={{ mb: 1 }}>
									<Typography
										variant="body2"
										sx={{
											fontFamily: "MAN EUROPE_Bold",
											color: "#333",
										}}
									>
										• {getPageDisplayName(pageKey)}
									</Typography>
									<Typography
										variant="caption"
										sx={{
											color: "#999",
											ml: 1,
											fontFamily: "MAN EUROPE_Regular",
										}}
									>
										First visit:{" "}
										{new Date(info.firstVisit).toLocaleDateString()}
									</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography
							variant="body2"
							sx={{
								color: "#999",
								fontStyle: "italic",
								ml: 2,
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							No pages visited yet
						</Typography>
					)}
				</Box>

				<Box sx={{ mt: 2 }}>
					<Button
						variant="outlined"
						color="warning"
						onClick={handleResetTours}
						sx={{
							borderColor: "#ff9800",
							color: "#ff9800",
							fontFamily: "MAN EUROPE_Bold",
							"&:hover": {
								borderColor: "#f57c00",
							},
						}}
					>
						Reset All Tours
					</Button>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							color: "#666",
							mt: 1,
							fontFamily: "MAN EUROPE_Regular",
						}}
					>
						This will allow all tours to be shown again on first visit
					</Typography>
				</Box>

				{showResetConfirm && (
					<Alert
						severity="null"
						sx={{ mt: 2 }}
						action={
							<Box>
								<Button
									size="small"
									onClick={confirmResetTours}
									sx={{
										color: "#cc0000",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Confirm
								</Button>
								<Button
									size="small"
									onClick={() => setShowResetConfirm(false)}
									sx={{
										ml: 1,
										color: "#1e2832",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Cancel
								</Button>
							</Box>
						}
					>
						<Typography sx={{ fontFamily: "MAN EUROPE_Regular" }}>
							Are you sure? This will reset all tour progress.
						</Typography>
					</Alert>
				)}
			</DialogContent>

			<DialogActions sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
				<Button
					onClick={onClose}
					sx={{
						color: "#666",
						fontFamily: "MAN EUROPE_Regular",
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					sx={{
						backgroundColor: "#cc0000",
						fontFamily: "MAN EUROPE_Bold",
						"&:hover": {
							backgroundColor: "#aa0000",
						},
					}}
				>
					Save Settings
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TourSettingsDialog;
